<template>
  <div class="my-data-container">
    <router-link to="/dashboard" class="btn">Go to dashboard</router-link>
    <h2>My Data Overview</h2>

    <!-- Coffee Consumption Summary -->
    <div class="data-card">
      <h3>Coffee Consumption</h3>
      <table class="data-table">
        <thead>
          <tr>
            <th>Consumption</th>
            <th>Today</th>
            <th>This Week</th>
            <th>This Month</th>
            <th>This Year</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Coffee</td>
            <td>{{ coffeeSummary.totalCoffeesToday }}</td>
            <td>{{ coffeeSummary.totalCoffeesThisWeek }} - ({{ dailyAverages.coffeeWeek }} pd)</td>
            <td>{{ coffeeSummary.totalCoffeesThisMonth }} - ({{ dailyAverages.coffeeMonth }} pd)</td>
            <td>{{ coffeeSummary.totalCoffeesThisYear }} - ({{ dailyAverages.coffeeYear }} pd)</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Sexual Activity Summary -->
    <div class="data-card" v-if="!loading && sexSummary.daily">
      <h3>Sexual Activity</h3>
      <table class="data-table">
        <thead>
          <tr>
            <th>Type</th>
            <th>Today</th>
            <th>This Week</th>
            <th>This Month</th>
            <th>This Year</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="Object.keys(sexSummary.daily).length === 0">
            <td colspan="5">No data available</td>
          </tr>
          <tr v-for="(value, key) in sexSummary.daily" :key="key">
            <td>{{ key }}</td>
            <td>{{ value }}</td>
            <td>{{ sexSummary.weekly[key] || 0 }} - ({{ dailyAverages.sexWeek[key] || 0 }} pd)</td>
            <td>{{ sexSummary.monthly[key] || 0 }} - ({{ dailyAverages.sexMonth[key] || 0 }} pd)</td>
            <td>{{ sexSummary.yearly[key] || 0 }} - ({{ dailyAverages.sexYear[key] || 0 }} pd)</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Toilet Activity Summary -->
    <div class="data-card" v-if="!loading && toiletSummary.daily">
      <h3>Toilet Activity</h3>
      <table class="data-table">
        <thead>
          <tr>
            <th>Type</th>
            <th>Today</th>
            <th>This Week</th>
            <th>This Month</th>
            <th>This Year</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="Object.keys(toiletSummary.daily).length === 0">
            <td colspan="5">No data available</td>
          </tr>
          <tr v-for="(value, key) in toiletSummary.daily" :key="key">
            <td>{{ key }}</td>
            <td>{{ value }}</td>
            <td>{{ toiletSummary.weekly[key] || 0 }} - ({{ dailyAverages.toiletWeek[key] || 0 }} pd)</td>
            <td>{{ toiletSummary.monthly[key] || 0 }} - ({{ dailyAverages.toiletMonth[key] || 0 }} pd)</td>
            <td>{{ toiletSummary.yearly[key] || 0 }} - ({{ dailyAverages.toiletYear[key] || 0 }} pd)</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Weight Summary -->
    <div class="data-card">
      <h3>Weight Overview</h3>
      <div class="weight-summary">
        <p>
          Latest Weight: <strong>{{ weightSummary.latestWeight }} kg</strong>
        </p>
        <p>
          Max Weight: <strong>{{ weightSummary.maxWeight }} kg</strong>
        </p>
        <p>
          Min Weight: <strong>{{ weightSummary.minWeight }} kg</strong>
        </p>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      loading: true,
      coffeeSummary: { totalCoffeesToday: 0, totalCoffeesThisWeek: 0, totalCoffeesThisMonth: 0, totalCoffeesThisYear: 0 },
      sexSummary: { today: { BJ: 0, HJ: 0, Sex: 0, 31: 0 }, weekly: {}, monthly: {}, yearly: {} },
      toiletSummary: { today: { pee: 0, poo: 0, both: 0 }, weekly: {}, monthly: {}, yearly: {} },
      weightSummary: { latestWeight: 0, maxWeight: 0, minWeight: 0 },
    };
  },
  computed: {
    dailyAverages() {
      const daysInWeek = 7;
      const today = new Date();
      const daysInMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
      const daysInYear = (today.getFullYear() % 4 === 0 && today.getFullYear() % 100 !== 0) || today.getFullYear() % 400 === 0 ? 366 : 365;

      return {
        coffeeWeek: (this.coffeeSummary.totalCoffeesThisWeek / daysInWeek).toFixed(2),
        coffeeMonth: (this.coffeeSummary.totalCoffeesThisMonth / daysInMonth).toFixed(2),
        coffeeYear: (this.coffeeSummary.totalCoffeesThisYear / daysInYear).toFixed(2),
        sexWeek: Object.fromEntries(Object.entries(this.sexSummary.weekly).map(([key, val]) => [key, (val / daysInWeek).toFixed(2)])),
        sexMonth: Object.fromEntries(Object.entries(this.sexSummary.monthly).map(([key, val]) => [key, (val / daysInMonth).toFixed(2)])),
        sexYear: Object.fromEntries(Object.entries(this.sexSummary.yearly).map(([key, val]) => [key, (val / daysInYear).toFixed(2)])),
        toiletWeek: Object.fromEntries(Object.entries(this.toiletSummary.weekly).map(([key, val]) => [key, (val / daysInWeek).toFixed(2)])),
        toiletMonth: Object.fromEntries(Object.entries(this.toiletSummary.monthly).map(([key, val]) => [key, (val / daysInMonth).toFixed(2)])),
        toiletYear: Object.fromEntries(Object.entries(this.toiletSummary.yearly).map(([key, val]) => [key, (val / daysInYear).toFixed(2)])),
      };
    },
  },
  mounted() {
    this.fetchAllData();
  },
  methods: {
    async fetchAllData() {
      this.loading = true;
      await Promise.all([this.fetchCoffeeSummary(), this.fetchSexSummary(), this.fetchToiletSummary(), this.fetchWeightSummary()]);
      this.loading = false;
    },
    async fetchCoffeeSummary() {
      const token = localStorage.getItem("token");
      const response = await fetch(`${process.env.VUE_APP_API_URL}/api/dashboard/data/coffee`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await response.json();
      this.coffeeSummary = data;
    },
    async fetchSexSummary() {
      const token = localStorage.getItem("token");
      const response = await fetch(`${process.env.VUE_APP_API_URL}/api/dashboard/data/sex`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await response.json();
      this.sexSummary = { ...this.sexSummary, ...data.currentUser };
    },
    async fetchToiletSummary() {
      const token = localStorage.getItem("token");
      const response = await fetch(`${process.env.VUE_APP_API_URL}/api/dashboard/data/toilet`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await response.json();
      this.toiletSummary = { ...this.toiletSummary, ...data.currentUser };
    },
    async fetchWeightSummary() {
      const token = localStorage.getItem("token");
      const response = await fetch(`${process.env.VUE_APP_API_URL}/api/dashboard/data/weight`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await response.json();
      this.weightSummary = data.weightSummary || { latestWeight: 0, maxWeight: 0, minWeight: 0 };
    },
  },
};
</script>

<style scoped>
.my-data-container {
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 16px;
  background: #f4f6f8;
}

h2,
h3 {
  color: #333;
}

.data-card {
  background: #fff;
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.data-card h3 {
  font-size: 1.2rem;
  color: #444;
  margin-bottom: 10px;
  border-bottom: 2px solid #4e73df;
  padding-bottom: 8px;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 8px;
}

.data-table th,
.data-table td {
  padding: 12px 8px;
  text-align: center;
}

.data-table th {
  background-color: black;
  color: #fff;
  font-weight: 600;
  font-size: 0.9rem;
}

.data-table td {
  border-bottom: 1px solid #ddd;
}

.weight-summary {
  font-size: 1rem;
  line-height: 1.5;
}

.trend-list {
  margin-top: 12px;
}

.trend-list h4 {
  font-size: 1rem;
  color: #333;
  margin-bottom: 8px;
}

.trend-list ul {
  list-style-type: none;
  padding: 0;
}

.trend-list li {
  font-size: 0.9rem;
  color: #666;
  padding: 4px 0;
  border-bottom: 1px solid #eee;
}

.btn {
  width: 180px;
  display: inline-block;
  padding: 10px 15px;
  background-color: black;
  color: white;
  border-radius: 5px;
  text-decoration: none;
  font-size: 16px;
  font-weight: 500;
  text-align: center;
  border: none;
  cursor: pointer;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  transition: background-color 0.3s ease;
}

@media (max-width: 600px) {
  .data-table th,
  .data-table td {
    padding: 8px 4px;
  }
}
</style>
